import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Send, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const safeFormat = (dateStr, fmt, opts) => {
  if (!dateStr) return '';
  try { const d = new Date(dateStr); if (isNaN(d.getTime())) return ''; return format(d, fmt, opts); } catch { return ''; }
};

export default function WhatsAppPage() {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (selectedLead) {
      fetchMessages(selectedLead.id);
    }
  }, [selectedLead]);

  const fetchLeads = async () => {
    try {
      const response = await api.getLeads();
      // Handle paginated response - extract leads array
      const leadsData = Array.isArray(response.data) ? response.data : (response.data?.leads || []);
      setLeads(leadsData);
      if (leadsData.length > 0 && !selectedLead) {
        setSelectedLead(leadsData[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar leads');
    }
  };

  const fetchMessages = async (leadId) => {
    try {
      const response = await api.getMessages(leadId);
      setMessages(response.data);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedLead) return;

    setSending(true);
    try {
      await api.sendMessage({
        lead_id: selectedLead.id,
        message: newMessage,
        is_from_lead: false
      });
      setNewMessage('');
      fetchMessages(selectedLead.id);
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)]">
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              WhatsApp
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Converse com seus leads
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100%-6rem)]">
            {/* Leads List */}
            <Card className="p-0 overflow-hidden">
              <div className="p-4 border-b bg-white">
                <h3 className="font-semibold text-slate-900">Conversas</h3>
              </div>
              <ScrollArea className="h-[calc(100%-4rem)]">
                {leads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className={`p-4 border-b cursor-pointer transition-colors ${
                      selectedLead?.id === lead.id
                        ? 'bg-emerald-50 border-l-4 border-l-emerald-600'
                        : 'hover:bg-slate-50'
                    }`}
                    data-testid={`chat-lead-${lead.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-emerald-100 text-emerald-700">
                          {lead.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{lead.name}</p>
                        <p className="text-sm text-slate-500 truncate">{lead.phone}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </Card>

            {/* Chat Area */}
            <Card className="lg:col-span-2 p-0 overflow-hidden flex flex-col">
              {selectedLead ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b bg-white flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback className="bg-emerald-100 text-emerald-700">
                        {selectedLead.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-slate-900">{selectedLead.name}</h3>
                      <p className="text-sm text-slate-500">{selectedLead.phone}</p>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-6 bg-slate-50">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_from_lead ? 'justify-start' : 'justify-end'}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-3 ${
                              msg.is_from_lead
                                ? 'bg-white text-slate-900'
                                : 'bg-emerald-600 text-white'
                            }`}
                          >
                            <p>{msg.message}</p>
                            <p
                              className={`text-xs mt-1 ${
                                msg.is_from_lead ? 'text-slate-500' : 'text-emerald-100'
                              }`}
                            >
                              {safeFormat(msg.created_at, 'HH:mm', { locale: ptBR })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t bg-white">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Digite sua mensagem..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        disabled={sending || !newMessage.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="send-message-button"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p>Selecione uma conversa para começar</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
